/**
 * Tiny, DOM-free geometry helpers for the art system:
 *  - parse + sample SVG path data (M/L/C/Q/Z, absolute or relative),
 *  - turn a centre-line into a filled, variable-width ("tapered") ink stroke,
 *  - shape builders (ellipse, rotated rounded rect) and colour mixing.
 */

export type Pt = { x: number; y: number };
type Seg = { kind: 'L' | 'C' | 'Q'; p: Pt[] };

/** Parse path data into one list of segments per subpath (first point included). */
export function parsePath(d: string): { start: Pt; segs: Seg[]; closed: boolean }[] {
  const tok = d.match(/[MLCQZmlcqz]|-?\d*\.?\d+(?:e-?\d+)?/g) ?? [];
  const out: { start: Pt; segs: Seg[]; closed: boolean }[] = [];
  let i = 0;
  let cmd = 'M';
  let cur: Pt = { x: 0, y: 0 };
  let sub: { start: Pt; segs: Seg[]; closed: boolean } | null = null;
  const num = () => Number(tok[i++]);
  const pt = (rel: boolean): Pt => {
    const x = num();
    const y = num();
    return rel ? { x: cur.x + x, y: cur.y + y } : { x, y };
  };
  while (i < tok.length) {
    const t = tok[i]!;
    if (/[a-zA-Z]/.test(t)) {
      cmd = t;
      i++;
      if (cmd === 'Z' || cmd === 'z') {
        if (sub) {
          sub.closed = true;
          sub.segs.push({ kind: 'L', p: [sub.start] });
          cur = sub.start;
        }
        continue;
      }
    }
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    if (C === 'M') {
      cur = pt(rel);
      sub = { start: cur, segs: [], closed: false };
      out.push(sub);
      cmd = rel ? 'l' : 'L';
    } else if (C === 'L') {
      const p = pt(rel);
      sub?.segs.push({ kind: 'L', p: [p] });
      cur = p;
    } else if (C === 'C') {
      const a = pt(rel);
      const b = pt(rel);
      const c = pt(rel);
      sub?.segs.push({ kind: 'C', p: [a, b, c] });
      cur = c;
    } else if (C === 'Q') {
      const a = pt(rel);
      const b = pt(rel);
      sub?.segs.push({ kind: 'Q', p: [a, b] });
      cur = b;
    } else {
      i++; // unsupported token: skip
    }
  }
  return out;
}

function evalSeg(from: Pt, s: Seg, t: number): Pt {
  const u = 1 - t;
  if (s.kind === 'L') return { x: from.x + (s.p[0]!.x - from.x) * t, y: from.y + (s.p[0]!.y - from.y) * t };
  if (s.kind === 'Q') {
    const [a, b] = s.p as [Pt, Pt];
    return { x: u * u * from.x + 2 * u * t * a.x + t * t * b.x, y: u * u * from.y + 2 * u * t * a.y + t * t * b.y };
  }
  const [a, b, c] = s.p as [Pt, Pt, Pt];
  return {
    x: u * u * u * from.x + 3 * u * u * t * a.x + 3 * u * t * t * b.x + t * t * t * c.x,
    y: u * u * u * from.y + 3 * u * u * t * a.y + 3 * u * t * t * b.y + t * t * t * c.y,
  };
}

/** Evenly-ish sampled polyline of the first subpath. */
export function samplePath(d: string, perSeg = 18): Pt[] {
  const sub = parsePath(d)[0];
  if (!sub) return [];
  const pts: Pt[] = [sub.start];
  let from = sub.start;
  for (const s of sub.segs) {
    const n = s.kind === 'L' ? 6 : perSeg;
    for (let k = 1; k <= n; k++) pts.push(evalSeg(from, s, k / n));
    from = s.kind === 'L' ? s.p[0]! : s.p[s.p.length - 1]!;
  }
  return pts;
}

export type Taper = 'both' | 'end' | 'start' | 'none';

/** Width multiplier along t in [0,1] for a taper profile. */
function profile(t: number, taper: Taper): number {
  const ease = (x: number) => Math.sin((Math.min(1, Math.max(0, x)) * Math.PI) / 2);
  if (taper === 'none') return 1;
  if (taper === 'end') return 0.18 + 0.82 * ease((1 - t) / 0.85);
  if (taper === 'start') return 0.18 + 0.82 * ease(t / 0.85);
  return 0.18 + 0.82 * ease(Math.min(t, 1 - t) / 0.42);
}

const f = (n: number) => Math.round(n * 100) / 100;

/**
 * A centre-line + max width → closed outline path of a brush-like ink stroke (thick in the
 * middle, thin at the ends, round caps). Rendered as a filled shape, so it never looks like a
 * uniform "vector" line.
 */
export function taperedStroke(d: string, width: number, taper: Taper = 'both'): string {
  const pts = samplePath(d);
  if (pts.length < 2) return '';
  // cumulative length → t
  const L = [0];
  for (let i = 1; i < pts.length; i++) L.push(L[i - 1]! + Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.y - pts[i - 1]!.y));
  const total = L[L.length - 1] || 1;
  const left: Pt[] = [];
  const right: Pt[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[Math.max(0, i - 1)]!;
    const b = pts[Math.min(pts.length - 1, i + 1)]!;
    let nx = -(b.y - a.y);
    let ny = b.x - a.x;
    const nl = Math.hypot(nx, ny) || 1;
    nx /= nl;
    ny /= nl;
    const w = (width * profile(L[i]! / total, taper)) / 2;
    left.push({ x: pts[i]!.x + nx * w, y: pts[i]!.y + ny * w });
    right.push({ x: pts[i]!.x - nx * w, y: pts[i]!.y - ny * w });
  }
  const wEnd = (width * profile(1, taper)) / 2;
  const wStart = (width * profile(0, taper)) / 2;
  let s = `M${f(left[0]!.x)} ${f(left[0]!.y)}`;
  for (let i = 1; i < left.length; i++) s += `L${f(left[i]!.x)} ${f(left[i]!.y)}`;
  const r = right.length - 1;
  s += `A${f(wEnd)} ${f(wEnd)} 0 0 1 ${f(right[r]!.x)} ${f(right[r]!.y)}`;
  for (let i = r - 1; i >= 0; i--) s += `L${f(right[i]!.x)} ${f(right[i]!.y)}`;
  s += `A${f(wStart)} ${f(wStart)} 0 0 1 ${f(left[0]!.x)} ${f(left[0]!.y)}Z`;
  return s;
}

/* --------------------------------- builders --------------------------------- */

/** Ellipse as a 4-arc cubic path (so it can be parsed, stroked and draw-on animated). */
export function ellipse(cx: number, cy: number, rx: number, ry: number = rx): string {
  const k = 0.5523;
  return (
    `M${f(cx - rx)} ${f(cy)}` +
    `C${f(cx - rx)} ${f(cy - ry * k)} ${f(cx - rx * k)} ${f(cy - ry)} ${f(cx)} ${f(cy - ry)}` +
    `C${f(cx + rx * k)} ${f(cy - ry)} ${f(cx + rx)} ${f(cy - ry * k)} ${f(cx + rx)} ${f(cy)}` +
    `C${f(cx + rx)} ${f(cy + ry * k)} ${f(cx + rx * k)} ${f(cy + ry)} ${f(cx)} ${f(cy + ry)}` +
    `C${f(cx - rx * k)} ${f(cy + ry)} ${f(cx - rx)} ${f(cy + ry * k)} ${f(cx - rx)} ${f(cy)}Z`
  );
}

/** Rotate a point around (cx, cy) by deg. */
export function rot(p: Pt, cx: number, cy: number, deg: number): Pt {
  const a = (deg * Math.PI) / 180;
  const dx = p.x - cx;
  const dy = p.y - cy;
  return { x: cx + dx * Math.cos(a) - dy * Math.sin(a), y: cy + dx * Math.sin(a) + dy * Math.cos(a) };
}

/** Polygon through points (rotated around c by deg), optionally closed. */
export function poly(points: [number, number][], deg = 0, c: Pt = { x: 0, y: 0 }, close = true): string {
  const ps = points.map(([x, y]) => (deg ? rot({ x, y }, c.x, c.y, deg) : { x, y }));
  return ps.map((p, i) => `${i ? 'L' : 'M'}${f(p.x)} ${f(p.y)}`).join('') + (close ? 'Z' : '');
}

/** Mirror path data horizontally around x = cx (absolute commands only). */
export function mirrorX(d: string, cx: number): string {
  let isX = true;
  return d.replace(/([MLCQZAmlcqza])|(-?\d*\.?\d+(?:e-?\d+)?)/g, (m, cmd) => {
    if (cmd) {
      isX = true;
      return m;
    }
    const v = Number(m);
    const r = isX ? f(2 * cx - v) : v;
    isX = !isX;
    return String(r);
  });
}

/** Translate path data (absolute commands only). */
export function shift(d: string, dx: number, dy: number): string {
  let isX = true;
  return d.replace(/([MLCQZmlcqz])|(-?\d*\.?\d+(?:e-?\d+)?)/g, (m, cmd) => {
    if (cmd) {
      isX = true;
      return m;
    }
    const v = Number(m) + (isX ? dx : dy);
    isX = !isX;
    return String(f(v));
  });
}

/* ---------------------------------- colour ---------------------------------- */

const hex = (h: string) => {
  const s = h.replace('#', '');
  const n = parseInt(s.length === 3 ? s.replace(/(.)/g, '$1$1') : s, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255] as const;
};

/** Mix colour a toward b by t (0 = a, 1 = b). */
export function mix(a: string, b: string, t: number): string {
  if (!a.startsWith('#') || !b.startsWith('#')) return a;
  const A = hex(a);
  const B = hex(b);
  return '#' + A.map((v, i) => Math.round(v + (B[i]! - v) * t).toString(16).padStart(2, '0')).join('');
}

/** Bumpy "cloud / canopy / mane" outline: n outward arcs around an ellipse (k = bulge). */
export function scallop(cx: number, cy: number, rx: number, ry: number, n: number, phase = 0, k = 1.3): string {
  const at = (a: number, r = 1) => [cx + Math.cos(a) * rx * r, cy + Math.sin(a) * ry * r] as const;
  const step = (Math.PI * 2) / n;
  let d = '';
  for (let i = 0; i < n; i++) {
    const a0 = phase + i * step;
    const a1 = a0 + step;
    const p0 = at(a0);
    const c0 = at(a0 + step * 0.18, k);
    const c1 = at(a1 - step * 0.18, k);
    const p1 = at(a1);
    if (!i) d += `M${f(p0[0])} ${f(p0[1])}`;
    d += `C${f(c0[0])} ${f(c0[1])} ${f(c1[0])} ${f(c1[1])} ${f(p1[0])} ${f(p1[1])}`;
  }
  return d + 'Z';
}

/**
 * Smooth closed (or open) curve through points (Catmull-Rom → cubic Bézier), tension 0..1.
 * The workhorse for organic silhouettes: list 5-10 points around the shape.
 */
export function blob(points: [number, number][], closed = true, tension = 1): string {
  const n = points.length;
  if (n < 2) return '';
  const P = (i: number) => (closed ? points[(i + n) % n]! : points[Math.max(0, Math.min(n - 1, i))]!);
  let d = `M${f(points[0]![0])} ${f(points[0]![1])}`;
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const p0 = P(i - 1);
    const p1 = P(i);
    const p2 = P(i + 1);
    const p3 = P(i + 2);
    const t = tension / 6;
    d += `C${f(p1[0] + (p2[0] - p0[0]) * t)} ${f(p1[1] + (p2[1] - p0[1]) * t)} ${f(p2[0] - (p3[0] - p1[0]) * t)} ${f(p2[1] - (p3[1] - p1[1]) * t)} ${f(p2[0])} ${f(p2[1])}`;
  }
  return closed ? d + 'Z' : d;
}

/** Remove the closing Z (turn a closed outline into an open centre-line, e.g. for ink rings). */
export const open = (d: string) => d.replace(/Z\s*$/i, '');

/** Scale path data around (cx, cy) (absolute commands only). */
export function scale(d: string, s: number, cx = 0, cy = 0, sy = s): string {
  let isX = true;
  return d.replace(/([MLCQZmlcqz])|(-?\d*\.?\d+(?:e-?\d+)?)/g, (m, cmd) => {
    if (cmd) {
      isX = true;
      return m;
    }
    const v = isX ? cx + (Number(m) - cx) * s : cy + (Number(m) - cy) * sy;
    isX = !isX;
    return String(f(v));
  });
}

/** Rotate path data around (cx, cy) by deg (absolute M/L/C/Q only). */
export function rotate(d: string, deg: number, cx: number, cy: number): string {
  const nums: number[] = [];
  const toks = d.match(/[MLCQZmlcqz]|-?\d*\.?\d+(?:e-?\d+)?/g) ?? [];
  let out = '';
  for (const t of toks) {
    if (/[A-Za-z]/.test(t)) {
      if (nums.length) throw new Error('rotate: odd coordinate count');
      out += t;
      continue;
    }
    nums.push(Number(t));
    if (nums.length === 2) {
      const p = rot({ x: nums[0]!, y: nums[1]! }, cx, cy, deg);
      out += `${f(p.x)} ${f(p.y)} `;
      nums.length = 0;
    }
  }
  return out.replace(/ ([A-Za-z])/g, '$1').trim();
}
